import React, { useState } from 'react';
import { Developer } from '../types/developer';
import emailjs from '@emailjs/browser';

interface Props {
  selectedDevelopers: Developer[];
  onSuccess: () => void;
  onOpenPrivacyModal: () => void;
  isLoading?: boolean;
  onHighlightSelection?: () => void;
}

const EmailCollectionSection: React.FC<Props> = ({
  selectedDevelopers,
  onSuccess,
  onOpenPrivacyModal,
  isLoading = false,
  onHighlightSelection
}) => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [highlightEmail, setHighlightEmail] = useState(false);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (emailError && validateEmail(value)) {
      setEmailError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const isEmailValid = email.trim() && validateEmail(email);
    const hasSelectedDevelopers = selectedDevelopers.length > 0;
    
    // Visual feedback for missing conditions
    if (!isEmailValid) {
      setHighlightEmail(true);
      setTimeout(() => setHighlightEmail(false), 1000);
      if (!email.trim()) {
        setEmailError('');
      } else {
        setEmailError('Please enter a valid email address');
      }
    }
    
    if (!hasSelectedDevelopers) {
      onHighlightSelection?.();
      // Remove the text warning but keep highlighting logic
      // if (!emailError) {
      //   setEmailError('Please select at least one developer');
      // }
    }
    
    // Don't proceed if conditions aren't met
    if (!isEmailValid || !hasSelectedDevelopers) {
      return;
    }
    
    setIsSubmitting(true);
    setEmailError('');

    try {
      // 🇸🇪 SAFE IMPLEMENTATION: Try Swedish template first, fallback to original if it fails
      let templateParams;
      
      try {
        // Import the new Swedish email formatter
        const { createSwedishEmailTemplate } = await import('../utils/emailFormatter');
        
        console.log('✅ Using Swedish email template');
        templateParams = createSwedishEmailTemplate(
          selectedDevelopers,
          email,
          'Värde kund' // "Valued client" in Swedish
        );
      } catch (swedishError) {
        console.warn('⚠️ Swedish template failed, using original template:', swedishError);
        
        // FALLBACK: Use original email template logic (100% backward compatible)
        const { createOriginalEmailTemplate } = await import('../utils/emailFormatter');
        templateParams = createOriginalEmailTemplate(selectedDevelopers, email);
      }

      // Debug EmailJS credentials
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      
      console.log('🔧 DEBUG EmailJS Config:', {
        publicKey: publicKey ? `${publicKey.slice(0, 5)}...` : 'MISSING',
        serviceId: serviceId ? `${serviceId.slice(0, 8)}...` : 'MISSING',
        templateId: templateId ? `${templateId.slice(0, 8)}...` : 'MISSING',
        hasTemplateParams: !!templateParams,
        recipientEmail: email,
        selectedDevsCount: selectedDevelopers.length
      });

      if (!publicKey || !serviceId || !templateId) {
        throw new Error('EmailJS credentials are missing from environment variables');
      }

      // Initialize and send email using EmailJS
      emailjs.init(publicKey);
      
      await emailjs.send(serviceId, templateId, templateParams as Record<string, unknown>);

      onSuccess();
    } catch (err) {
      console.error('Error sending email:', err);
      setEmailError('Failed to send email. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = email.trim() && validateEmail(email) && selectedDevelopers.length > 0;
  const isProcessing = isLoading || isSubmitting;

    return (
    <div className="max-w-4xl mx-auto">
      <form onSubmit={handleSubmit} className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-2 lg:gap-5">
        {/* Email Input */}
        <div className="flex-1 min-w-0 w-full lg:w-auto">
          <label className="sr-only">Email</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={handleEmailChange}
            placeholder="Din e-post *"
            disabled={isProcessing}
            className={`w-full px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 shadow-sm transition-all duration-300 ${
              highlightEmail 
                ? 'border-red-500 shadow-red-200 shadow-lg animate-pulse' 
                : emailError 
                ? 'border-red-300' 
                : 'border-gray-300'
            }`}
          />
          {emailError && (
            <p className="text-red-600 text-xs mt-1">{emailError}</p>
          )}
        </div>
        
        {/* Privacy Policy Text - Desktop: between email and button, Mobile: below button */}
        <div className="hidden lg:flex items-start space-x-3 lg:flex-shrink-0 lg:w-auto lg:max-w-xs">
          <p className="text-sm sm:text-xs text-gray-600 leading-relaxed">
            Jag godkänner{' '}
            <button
              type="button"
              onClick={onOpenPrivacyModal}
              className="text-gray-600 hover:text-gray-800 cursor-pointer bg-transparent border-none p-0"
            >
              Integritetspolicyn
            </button>
          </p>
        </div>
        
        {/* Mobile: Button and Privacy Text Row */}
        <div className="flex lg:hidden items-start gap-2 w-full">
          {/* Submit Button */}
          <button
            type="submit"
            disabled={isProcessing}
            className={`px-5 py-2 text-sm font-bold rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 whitespace-nowrap flex-shrink-0 shadow-lg cursor-pointer ${
              isFormValid && !isProcessing
                ? 'bg-green-600 text-white hover:bg-green-700 hover:shadow-xl focus:ring-green-400'
                : 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-400'
            }`}
          >
            {isProcessing ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-1 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-xs">Skickar...</span>
              </div>
            ) : (
              <span className="text-xs">Skicka CV:n</span>
            )}
          </button>
          
          {/* Privacy Policy Text - Mobile: to the right of button */}
          <p className="text-xs leading-tight text-gray-600 flex-1 mt-0.5">
            Jag godkänner{' '}
            <button
              type="button"
              onClick={onOpenPrivacyModal}
              className="text-gray-600 hover:text-gray-800 cursor-pointer bg-transparent border-none p-0 text-xs"
            >
              Integritetspolicyn
            </button>
          </p>
        </div>
        
        {/* Desktop Submit Button - Original size */}
        <button
          type="submit"
          disabled={isProcessing}
          className={`hidden lg:block px-8 py-3 sm:px-6 sm:py-2 text-lg sm:text-base font-bold rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 whitespace-nowrap flex-shrink-0 shadow-lg cursor-pointer ${
            isFormValid && !isProcessing
              ? 'bg-green-600 text-white hover:bg-green-700 hover:shadow-xl focus:ring-green-400'
              : 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-400'
          }`}
        >
          {isProcessing ? (
            <div className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-2 sm:mr-1 h-5 w-5 sm:h-4 sm:w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-lg sm:text-base">Skickar...</span>
            </div>
          ) : (
            <span className="text-lg sm:text-base">Skicka CV:n</span>
          )}
        </button>
      </form>
    </div>
  );
};

export default EmailCollectionSection; 